import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Appointment } from "../../../types";
import { appointmentsApi } from "../../../services";
import { useCompany } from "../../../contexts/CompanyContext";
import {
  Card,
  ErrorState,
  Loading,
} from "../../shared";
import { Icons } from "../../shared/Icons";
import { AdminPageHeader } from "../shared";
import { MonthPeriodPicker } from "../AdminDashboard/MonthPeriodPicker";
import {
  getCalendarMonthWeeks,
  getTodayInBrazil,
  getTodayMonthYearBrazil,
  eachYmdInclusive,
} from "../../../utils/dateUtils";
import { buildServiceColorMap, getServiceColorByIndex } from "../../../utils/serviceColors";
import { CarBrandLogo } from "../../shared/CarBrandLogo";

const WEEKDAY_LABELS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function clientFirstName(name?: string | null): string {
  if (!name?.trim()) return "Cliente";
  return name.trim().split(/\s+/)[0];
}

function appointmentDateKey(appointment: Appointment): string {
  return appointment.appointmentDate.split("T")[0];
}

function appointmentEndKey(appointment: Appointment): string {
  return (appointment.appointmentEnd || appointment.appointmentDate).split("T")[0];
}

export const AdminAppointmentsMonthCalendar: React.FC = () => {
  const navigate = useNavigate();
  const { company } = useCompany();
  const today = getTodayInBrazil();
  const initial = getTodayMonthYearBrazil();

  const [month, setMonth] = useState(initial.month);
  const [year, setYear] = useState(initial.year);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadMonth = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await appointmentsApi.getMonthAppointments(year, month);
      if (res.success && res.data) {
        setAppointments(res.data);
      } else {
        setAppointments([]);
        setError(res.error || "Erro ao carregar calendário do mês");
      }
    } catch (e) {
      setAppointments([]);
      setError(
        e instanceof Error ? e.message : "Erro ao carregar calendário do mês"
      );
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    if (company?.id) {
      loadMonth();
    }
  }, [company?.id, loadMonth]);

  const weeks = useMemo(() => getCalendarMonthWeeks(year, month), [year, month]);

  const byDate = useMemo(() => {
    const map = new Map<string, Appointment[]>();
    for (const apt of appointments) {
      const dates = eachYmdInclusive(
        appointmentDateKey(apt),
        appointmentEndKey(apt)
      );
      for (const key of dates) {
        const list = map.get(key) ?? [];
        list.push(apt);
        map.set(key, list);
      }
    }
    return map;
  }, [appointments]);

  const serviceColors = useMemo(
    () =>
      buildServiceColorMap(
        appointments.map((apt) => apt.serviceId || apt.service?.id || 0)
      ),
    [appointments]
  );

  const serviceLegend = useMemo(() => {
    const seen = new Map<number, string>();
    for (const apt of appointments) {
      const id = apt.serviceId || apt.service?.id;
      if (!id || seen.has(id)) continue;
      seen.set(id, apt.service?.name ?? `Serviço #${id}`);
    }
    return Array.from(seen.entries())
      .sort((a, b) => a[1].localeCompare(b[1], "pt-BR"))
      .map(([id, name]) => ({
        id,
        name,
        color: serviceColors.get(id)!,
      }));
  }, [appointments, serviceColors]);

  const monthPrefix = `${year}-${String(month).padStart(2, "0")}`;
  const totalInMonth = appointments.length;

  const openDay = (date: string) => {
    sessionStorage.setItem("adminAppointmentsSelectedDate", date);
    navigate("/admin/appointments");
  };

  return (
    <div className="space-y-4 sm:space-y-6 md:space-y-8 animate-fade-in">
      <AdminPageHeader
        icon={<Icons.CalendarMonth sx={{ fontSize: 28 }} />}
        title="Calendário do Mês"
        subtitle={
          loading
            ? "Carregando resumo do mês…"
            : `${totalInMonth} ${totalInMonth === 1 ? "agendamento" : "agendamentos"} no mês · clique em um dia para abrir`
        }
      />

      <Card className="p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <MonthPeriodPicker
            month={month}
            year={year}
            onChange={(nextMonth, nextYear) => {
              setMonth(nextMonth);
              setYear(nextYear);
            }}
            disabled={loading}
            className="w-full sm:w-auto sm:max-w-xs"
          />
          <button
            type="button"
            onClick={loadMonth}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50"
          >
            <Icons.Refresh sx={{ fontSize: 18 }} />
            Atualizar
          </button>
        </div>

        {serviceLegend.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {serviceLegend.map((item) => (
              <span
                key={item.id}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${item.color.bg} ${item.color.text} ${item.color.border}`}
              >
                <span className={`w-2 h-2 rounded-full ${item.color.dot}`} />
                {item.name}
              </span>
            ))}
          </div>
        )}
      </Card>

      {error && (
        <ErrorState message={error} variant="card" onRetry={loadMonth} />
      )}

      {loading && !error ? (
        <Loading
          message="Carregando calendário…"
          size="xl"
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 p-12"
        />
      ) : (
        <Card className="p-2 sm:p-4 overflow-x-auto">
          <div className="min-w-[720px]">
            <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-1 sm:mb-2">
              {WEEKDAY_LABELS.map((label) => (
                <div
                  key={label}
                  className="text-center text-xs sm:text-sm font-semibold text-gray-500 dark:text-slate-400 py-1"
                >
                  {label}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1 sm:gap-2">
              {weeks.flatMap((week) =>
                week.map((date) => {
                  const inMonth = date.startsWith(monthPrefix);
                  const dayAppointments = byDate.get(date) ?? [];
                  const isToday = date === today;
                  const dayNumber = parseInt(date.split("-")[2], 10);

                  return (
                    <button
                      key={date}
                      type="button"
                      onClick={() => openDay(date)}
                      className={`rounded-lg border p-1 sm:p-1.5 text-left transition-all hover:shadow-md hover:border-sky-300 dark:hover:border-sky-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 ${
                        inMonth
                          ? "bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-600"
                          : "bg-gray-50/80 dark:bg-slate-900/60 border-gray-100 dark:border-slate-700 opacity-60"
                      } ${isToday ? "ring-2 ring-sky-400 dark:ring-sky-500 border-sky-300 dark:border-sky-500" : ""}`}
                    >
                      <div className="flex items-center justify-between gap-1 leading-none">
                        <span
                          className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 text-xs font-semibold ${
                            isToday
                              ? "rounded-full bg-sky-600 text-white px-1"
                              : inMonth
                                ? "text-gray-900 dark:text-white"
                                : "text-gray-400 dark:text-slate-500"
                          }`}
                        >
                          {dayNumber}
                        </span>
                        {dayAppointments.length > 0 && (
                          <span className="text-[10px] font-bold text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-950 border border-sky-100 dark:border-sky-800 px-1 py-0 rounded-full leading-4">
                            {dayAppointments.length}
                          </span>
                        )}
                      </div>

                      {dayAppointments.length > 0 && (
                        <div className="mt-0.5 space-y-0.5 overflow-hidden">
                          {dayAppointments.slice(0, 4).map((apt) => {
                            const serviceId =
                              apt.serviceId || apt.service?.id || 0;
                            const color =
                              serviceColors.get(serviceId) ??
                              getServiceColorByIndex(0);
                            return (
                              <div
                                key={apt.id}
                                className={`flex items-center gap-1 rounded border px-1 py-0 ${color.bg} ${color.text} ${color.border}`}
                                title={`${apt.client?.name ?? "Cliente"} · ${apt.service?.name ?? "Serviço"}`}
                              >
                                {company?.category === "car" &&
                                apt.clientCar?.brand ? (
                                  <CarBrandLogo
                                    brand={apt.clientCar.brand}
                                    size={12}
                                    className="shrink-0"
                                  />
                                ) : null}
                                <span className="truncate text-[10px] sm:text-xs font-medium leading-4">
                                  {clientFirstName(apt.client?.name)}
                                </span>
                              </div>
                            );
                          })}
                          {dayAppointments.length > 4 && (
                            <p className="text-[10px] text-gray-500 dark:text-slate-400 font-medium leading-4">
                              +{dayAppointments.length - 4} mais
                            </p>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default AdminAppointmentsMonthCalendar;
